import {
  Finding,
  FindingStatus,
  ConflictCategory,
  ConflictType,
  StatusVariant,
} from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components';

import { FindingActionButton } from './finding-action-button';

// Mapping for finding status to display configuration
const findingStatusMapping = {
  [FindingStatus.NOT_RELEVANT]: { text: 'Not Relevant', variant: StatusVariant.INFO },
  [FindingStatus.ACTIVE]: { text: 'Active', variant: StatusVariant.SUCCESS },
  [FindingStatus.PROCESSING]: { text: 'Processing', variant: StatusVariant.WARNING },
  [FindingStatus.PROCESSED]: { text: 'Processed', variant: StatusVariant.INFO },
  [FindingStatus.DELETED]: { text: 'Deleted', variant: StatusVariant.ERROR },
} as const;

// Mapping for conflict category to display configuration
const conflictCategoryMapping = {
  [ConflictCategory.RISK]: { text: 'Risk', variant: StatusVariant.ERROR },
  [ConflictCategory.INSIGHT]: { text: 'Insight', variant: StatusVariant.INFO },
  [ConflictCategory.PROPERTY]: { text: 'Property', variant: StatusVariant.WARNING },
  [ConflictCategory.COMPANY]: { text: 'Company', variant: StatusVariant.WARNING },
  [ConflictCategory.RENTAL_AGREEMENT]: {
    text: 'Rental Agreement',
    variant: StatusVariant.INFO,
  },
  [ConflictCategory.MISSING_DOCUMENTATION]: {
    text: 'Missing Documentation',
    variant: StatusVariant.ERROR,
  },
  [ConflictCategory.GAPS]: { text: 'Gaps', variant: StatusVariant.WARNING },
} as const;

/**
 * Column definitions for the findings table
 */
export const findingsColumns: ColumnDef<Finding>[] = [
  {
    header: '',
    accessorKey: 'conflictCategory',
    cell: ({ getValue }) => {
      const category = getValue() as ConflictCategory;
      const config = conflictCategoryMapping[category];
      return <Badge text={config.text} variant={config.variant} />;
    },
  },
  {
    header: 'Conflict Type',
    accessorKey: 'conflictType',
    cell: ({ getValue }) => {
      const conflictType = getValue() as ConflictType;
      return <span>{formatEnumToReadableText(conflictType)}</span>;
    },
  },
  {
    header: 'File',
    accessorKey: 'fileName',
    cell: ({ getValue }) => {
      const fileName = getValue() as string | null;
      return <span>{fileName || 'Unknown'}</span>;
    },
  },
  {
    header: 'Processor',
    accessorKey: 'processorName',
    cell: ({ getValue }) => {
      const processorName = getValue() as string;
      return <span>{formatEnumToReadableText(processorName)}</span>;
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ getValue }) => {
      const status = getValue() as FindingStatus;
      const config = findingStatusMapping[status];
      return <Badge text={config.text} variant={config.variant} />;
    },
  },
  {
    header: '',
    accessorKey: 'id',
    cell: ({ row }) => <FindingActionButton finding={row.original} />,
  },
];
